import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Button } from 'components/Form/Button/Button';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';

import { getLineItemDisplay } from '../../helpers/lineItemDisplay';
import { LineItemEntry, LineItemId, TransportUnitId } from '../../types';
import { ItemActionsMenu } from '../ItemActionsMenu/ItemActionsMenu';
import { LineItemIdentifiers } from '../LineItemIdentifiers/LineItemIdentifiers';

type PlacedItemProps = {
  entry: LineItemEntry;
  quantity: number;
  tuId: TransportUnitId;
  isSelected: boolean;
  onToggleSelect: () => void;
  onRemoveItem: (lineItemId: LineItemId, tuId: TransportUnitId) => void;
  onMoveTo: () => void;
  onEditQuantity: () => void;
};

/**
 * i18n keys
 * t('webedi:packing_list.transport_unit.item_quantity', { quantity })
 * t('webedi:packing_list.transport_unit.item_quantity_with_unit', { quantity, unit })
 */
export const PlacedItem = ({
  entry,
  quantity,
  tuId,
  isSelected,
  onToggleSelect,
  onRemoveItem,
  onMoveTo,
  onEditQuantity,
}: PlacedItemProps) => {
  const { t } = useTranslation();
  const { name, identifiers, unitOfMeasure } = getLineItemDisplay(entry.row, t);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `placed:${tuId}:${entry.id}`,
    data: { kind: 'placed', tuId, lineItemId: entry.id, quantity },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'flex cursor-grab items-center justify-between rounded-md bg-gray-50 p-3 text-sm active:cursor-grabbing',
        isDragging ? 'opacity-40' : '',
        isSelected ? 'ring-2 ring-procuros-green-500' : '',
      )}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelect}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={name}
        />
        <div className="min-w-0">
          <p className="truncate text-gray-900">{name}</p>
          <LineItemIdentifiers parts={identifiers} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge color={BadgeColor.gray}>
          {unitOfMeasure
            ? t('webedi:packing_list.transport_unit.item_quantity_with_unit', { quantity, unit: unitOfMeasure })
            : t('webedi:packing_list.transport_unit.item_quantity', { quantity })}
        </Badge>
        <ItemActionsMenu onMoveTo={onMoveTo} onEditQuantity={onEditQuantity} />
        <Button
          iconOnly
          variant="minimal"
          size="small"
          LeftIcon={XMarkIcon}
          aria-label={t('webedi:packing_list.transport_unit.remove_item')}
          analyticsId="webedi:packing_list_item_remove"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onRemoveItem(entry.id, tuId)}
        >
          {t('webedi:packing_list.transport_unit.remove_item')}
        </Button>
      </div>
    </div>
  );
};
